import React from 'react';
import PropTypes from 'prop-types';
import { FileAddOutlined } from '@ant-design/icons';
import { Modal, Button, Upload} from 'antd';
import { AttachmentList } from './attachmentList';
import { useAttachmentsField } from './hooks/useAttachmentsField';
import { useDictionaryTranslation } from '../../../../hooks/useDictionaryTranslation';
import { useNotifications } from '../../../../hooks/useNotifications';
import { FormWrapper } from '../../../formWrapper/form-wrapper';

const { Dragger } = Upload;

export const ModalAddAttachment = ({ title, show, limit, handleClose, defaultValue = [], uploadFile }) => {
  const { translate: t } = useDictionaryTranslation();
  const { showConfirmNotification } = useNotifications();
  const { attachmentList, uploadAttachment, getValue, removeAttachment, hasChanges } = useAttachmentsField({
    defaultValue,
    limit,
    uploadFile,
  });
  const isLoading = attachmentList.some(item => item.loading);
  const hasError = attachmentList.some(item => item.hasError);

  const closeModalAndSave = () => {
    const attachmentsValue = getValue();
    handleClose({ saveValues: true, value: attachmentsValue });
  };

  const confirmSkipFilesWithError = () => {
    showConfirmNotification({
      title: 'areYouSure',
      content: 'filesWithErrorWillNotBeSaved',
      onOk: () => {
        closeModalAndSave();
      },
    });
  };

  const confirmSkipFilesUploading = () => {
    showConfirmNotification({
      title: 'areYouSure',
      content: 'filesUploadingWillNotBeSaved',
      onOk: () => {
        closeModalAndSave();
      },
    });
  };

  const confirmNotToSaveChanges = () => {
    showConfirmNotification({
      title: 'areYouSure',
      content: 'changesWillNotBeSaved',
      onOk: () => {
        handleClose();
      },
    });
  };

  const saveItem = () => {
    if (hasError) {
      confirmSkipFilesWithError();
      return;
    }
    if (isLoading) {
      confirmSkipFilesUploading();
      return;
    }
    closeModalAndSave();
  };

  const closeModal = () => {
    if (hasChanges) {
      confirmNotToSaveChanges();
      return;
    }
    handleClose();
  };

  const getRemoveItemHandler = ({ id, filename }) => () => {
    showConfirmNotification({
      title: 'areYouSure',
      content: t('attachmentWillBeRemoved', { filename }),
      onOk: () => {
        removeAttachment(id);
      },
    });
  };

  const getRetryUploadItemHandler = attachment => () => {
    uploadAttachment({ attachmentId: attachment.id, file: attachment.file });
  };

  return (
    <Modal
      maskClosable={false}
      title={title}
      centered
      width="60%"
      height="70%"
      onCancel={closeModal}
      destroyOnClose
      visible={show}
      footer={
        <Button key="submit" htmlType="submit" type="primary" onClick={saveItem}>
          {t('accept')}
        </Button>
      }
    >
      <FormWrapper>
        <Dragger
          showUploadList={false}
          multiple
          fileList={attachmentList}
          customRequest={uploadAttachment}
          maxCount={limit}
        >
          <p>
            <FileAddOutlined style={{ fontSize: '50px' }} />
          </p>
          <p>{t('clickOrDragToUpload')}</p>
        </Dragger>
        <AttachmentList defaultValue={attachmentList} getRemoveItemHandler={getRemoveItemHandler} getRetryUploadItemHandler={getRetryUploadItemHandler}/>
      </FormWrapper>
    </Modal>
  );
};

ModalAddAttachment.propTypes = {
  show: PropTypes.bool,
  title: PropTypes.string,
  handleClose: PropTypes.func,
  limit: PropTypes.number,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string, // this naming case come from tapptus v1
      url: PropTypes.string,
    }),
  ),
  uploadFile: PropTypes.func,
};
